import axios from 'axios';
import { createApiClient } from '../../api/client';
import {
  PendingPaymentSchema,
  type Decision,
  type PendingPayment,
} from './schema';

/**
 * card-payment-sca talks to the `cardpaymentsca` microservice REST API
 * (`/api/v1/cardpaymentsca/*`). Contract source:
 * `flows/card_payment_SCA/API_CONTRACT.md` (authoritative — see PRD § 7).
 *
 * Auth: standard authenticated embed. The host-injected bearer JWT (carrying
 * the `CAN_SEE_SCA_TEST` authority) travels in the `Authorization` header,
 * attached by `createApiClient` via `setAuthToken` — never in the body, never
 * set in flow code. Deployment-context headers (`x-customerid`,
 * `marketplacekey`, `x-solution`) are merged by the client from `init.headers`.
 *
 * Base URL is `VITE_API_URL` from `.env.<mode>` — empty in dev / unit tests so
 * MSW (relative-path handlers) intercepts; per-env builds point it at the real
 * service. The microservice is deployed, so the real embed always hits it.
 */
const API_BASE = import.meta.env.VITE_API_URL ?? '';
export const PENDING_PAYMENTS_PATH = '/api/v1/cardpaymentsca/pending-payments';
export const PAYMENT_DECISIONS_PATH = '/api/v1/cardpaymentsca/payment-decisions';

const apiClient = () => createApiClient(API_BASE);

// --- GET /pending-payments -------------------------------------------------

type PendingPaymentsApiResponse = { pendingPayments?: unknown[] };

/**
 * List the SCA challenges awaiting a decision. Items that don't satisfy the
 * contract (e.g. missing `timerInfo`) are dropped rather than failing the whole
 * list — they're non-actionable without a countdown (PRD § 6).
 */
export const fetchPendingPayments = async (): Promise<PendingPayment[]> => {
  const { data } = await apiClient().get<PendingPaymentsApiResponse>(
    PENDING_PAYMENTS_PATH,
  );
  const raw = Array.isArray(data?.pendingPayments) ? data.pendingPayments : [];
  return raw.flatMap((item) => {
    const parsed = PendingPaymentSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  });
};

// --- POST /payment-decisions/{notificationId} ------------------------------

export type PaymentDecisionResult = {
  status: string;
  decision: Decision;
  errorCode: string | null;
  merchantName?: string;
  /** Backend-authored success copy, surfaced verbatim (no front-side mapping). */
  message?: string;
};

/**
 * Submit the cardholder's approve/decline decision for one challenge. Releases
 * or blocks the underlying card payment via the upstream SCA service
 * (irreversible). Not idempotent: a second decision on the same id returns
 * `422 SCA_NOTIFICATION_USER_DECISION_ALREADY_MADE`.
 *
 * `merchantName` is the pending item's `title`, forwarded in the body so the BE
 * can embed it in the result copy — the decision endpoint doesn't otherwise
 * have it (SEB-1618).
 */
export const submitDecision = async (
  notificationId: string,
  decision: Decision,
  merchantName?: string,
): Promise<PaymentDecisionResult> => {
  const { data } = await apiClient().post<PaymentDecisionResult>(
    `${PAYMENT_DECISIONS_PATH}/${encodeURIComponent(notificationId)}`,
    merchantName ? { decision, merchantName } : { decision },
  );
  return {
    status: data.status,
    decision: data.decision,
    errorCode: data.errorCode ?? null,
    merchantName: data.merchantName,
    message: data.message,
  };
};

// --- Error extraction ------------------------------------------------------

export type ApiError = {
  /** HTTP status (undefined for a transport/network error with no response). */
  status?: number;
  /** RFC-7807 `errorCode` when present (e.g. the `SCA_*` business code). */
  errorCode: string | null;
  /** Backend-authored copy to surface verbatim (no front-side mapping). */
  message?: string;
  /** RFC-7807 `detail`, used as a fallback when `message` is absent. */
  detail?: string;
};

type ProblemDetail = {
  errorCode?: string;
  message?: string;
  detail?: string;
  type?: string;
};

/**
 * Normalise an unknown thrown error into `{ status, errorCode, message, detail }`.
 * The UI surfaces the backend `message` verbatim (no front-side copy mapping)
 * and uses `status` to tell transient errors (no response / 5xx) apart from
 * business ones (PRD § 9).
 */
export const parseApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as ProblemDetail | undefined;
    return {
      status: error.response?.status,
      errorCode: body?.errorCode ?? null,
      message: body?.message,
      detail: body?.detail,
    };
  }
  return { errorCode: null };
};
